import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { apiService } from '../../../../shared/services/api'
import { truncateMiddle } from '../../../../shared/lib/analytics-format'
import type { AnalyticsLinkListItem } from '../../../../shared/types/analytics'
import { useTranslation } from 'react-i18next'

interface LinkPickerProps {
  onSelect: (shortCode: string) => void
  selectedShortCode?: string
  placeholder?: string
}

const SEARCH_DEBOUNCE_MS = 250
const VISIBLE_RESULTS = 8

/** 검색 가능한 링크 선택기 — 대시보드를 거치지 않고 바로 링크 분석으로 이동 */
export default function LinkPicker({
  onSelect,
  selectedShortCode,
  placeholder,
}: LinkPickerProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics', 'links', debounced],
    queryFn: () => apiService.getAnalyticsLinks(debounced),
    staleTime: 60_000,
  })

  const results = useMemo(() => (data ?? []).slice(0, VISIBLE_RESULTS), [data])

  const handleSelect = (link: AnalyticsLinkListItem) => {
    setIsOpen(false)
    setSearch('')
    onSelect(link.shortCode)
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="sr-only" htmlFor="link-picker">
        {t('bookmark.search')}
      </label>
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          id="link-picker"
          type="search"
          value={search}
          placeholder={placeholder ?? t('bookmark.picker.placeholder')}
          onChange={event => setSearch(event.target.value)}
          onFocus={() => setIsOpen(true)}
          className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {isLoading && (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              {t('bookmark.picker.loading')}
            </p>
          )}
          {isError && (
            <p className="px-4 py-3 text-sm text-rose-600 dark:text-rose-400">
              {t('bookmark.picker.error')}
            </p>
          )}
          {!isLoading && !isError && results.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              {t('bookmark.picker.empty')}
            </p>
          )}
          <ul>
            {results.map(link => (
              <li key={link.shortCode}>
                <button
                  type="button"
                  onClick={() => handleSelect(link)}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition hover:bg-primary-50 dark:hover:bg-primary-950/40 ${
                    link.shortCode === selectedShortCode
                      ? 'bg-primary-50/70 dark:bg-primary-950/30'
                      : ''
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                      {link.title}
                    </span>
                    <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                      /{link.shortCode} · {truncateMiddle(link.originalUrl, 44)}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                    {t('bookmark.picker.clicks', {
                      count: link.clicks.toLocaleString(),
                    })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
